import { Component } from '@theme/component';
import { Wishlist, WishlistUpdateEvent } from '@theme/wishlist';

/**
 * A custom element that displays a wishlist button (heart icon).
 * Allows users to add/remove products from their wishlist.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} icon - The heart icon element.
 *
 * @extends {Component<Refs>}
 */
class WishlistButton extends Component {
  /** @type {string} */
  get productId() {
    return this.getAttribute('product-id') || '';
  }

  /** @type {string} */
  get productHandle() {
    return this.getAttribute('product-handle') || '';
  }

  /** @type {boolean} */
  get isInWishlist() {
    return Wishlist.hasProduct(this.productId);
  }

  connectedCallback() {
    super.connectedCallback();
    this.updateState();
    document.addEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);
  }

  /**
   * Updates the visual state based on whether the product is in the wishlist.
   */
  updateState() {
    const isInWishlist = this.isInWishlist;
    this.classList.toggle('wishlist-button--active', isInWishlist);
    this.setAttribute('aria-pressed', String(isInWishlist));

    const label = isInWishlist ? 'Remove from wishlist' : 'Add to wishlist';
    this.setAttribute('aria-label', label);
    this.setAttribute('title', label);
  }

  /**
   * Handles the click event to toggle the wishlist state.
   * @param {MouseEvent} event
   */
  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const wasAdded = Wishlist.toggleProduct(this.productId, this.productHandle);

    // Add animation class
    this.classList.add('wishlist-button--animating');
    setTimeout(() => {
      this.classList.remove('wishlist-button--animating');
    }, 300);
  }

  /**
   * Handles wishlist update events from other sources.
   * @param {WishlistUpdateEvent} event
   */
  onWishlistUpdate = (event) => {
    this.updateState();
  };
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
